/**
 * lib/collectionCheck.ts
 * ------------------------------------------------------------------
 * HR PAYROLL — "did we collect what we expected?", asked before any
 * payslip is emailed.
 *
 * In 2026 a pay run went out with school-loan errors that a glance at
 * the loan total would have caught: the total was not what the bursary
 * expected to collect, and nobody compared. So emailing payslips now
 * waits behind one tick per collection (school loans, salary advances,
 * medical aid, PAYE, …), each against its total.
 *
 * WHY LAST MONTH, AND NOT THE PORTAL'S OWN SCHEDULE. The portal's
 * "expected" amount comes from the same loan set-up that produced the
 * payslip, so a loan entered with the wrong start month agrees with
 * itself and nothing would be flagged. Last month's run is independent
 * of this month's set-up, so each collection is shown beside it, with
 * every person whose deduction started, stopped or changed. A loan
 * that STOPPED while last month's slip still showed a balance owing is
 * the specific failure of 2026 and is marked as such.
 *
 * Pure: takes payslip lines in, returns collections out. A collection is
 * keyed by the line `code` (SCHOOL_LOAN, MEDICAL_AID, PAYE, …), the one
 * thing every payslip line carries.
 * ------------------------------------------------------------------ */
import {
  DEDUCTION_TYPE_LABELS, DEDUCTION_TYPE_ORDER, type DeductionType, type PayslipLine,
} from '@/lib/hrPayroll';

export interface RunSlip {
  staffId: string;
  name: string;
  lines: PayslipLine[];
}

export type ChangeKind =
  /** Nothing to look at. */
  | 'same'
  /** Deducted this month and not last month. */
  | 'added'
  /** Deducted last month, not this month, and nothing was left owing. */
  | 'stopped'
  /** Deducted last month, not this month, yet last month's slip showed a
   *  balance still owing. The 2026 failure. */
  | 'owing'
  /** Deducted both months, by different amounts. */
  | 'changed';

export interface CollectionRow {
  staffId: string;
  name: string;
  last: number;
  now: number;
  change: number;
  /** Balance left after this month (balance-carrying deductions only). */
  balanceLeft?: number;
  /** Balance left after last month — what a stopped loan still owes. */
  lastBalance?: number;
  /** The payslip line labels, when they say more than the collection does
   *  (which bank, which loan). */
  detail?: string;
  kind: ChangeKind;
}

export interface Collection {
  key: string;
  label: string;
  /** Carries a balance (a loan or advance), so "balance left" means something. */
  carriesBalance: boolean;
  last: number;
  now: number;
  change: number;
  /** Staff deducted this month. */
  staffCount: number;
  rows: CollectionRow[];
  counts: Record<Exclude<ChangeKind, 'same'>, number>;
  /** bad: a loan stopped with money owing; warn: anything changed; ok: identical. */
  state: 'ok' | 'warn' | 'bad';
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const CENT = 0.005;

function labelFor(code: string, fallback: string): string {
  if (code === 'PAYE') return 'PAYE';
  if (code === 'GRA_TAX') return 'Gratuity tax';
  const t = code.toLowerCase() as DeductionType;
  return DEDUCTION_TYPE_LABELS[t] ?? fallback;
}

function orderOf(code: string): number {
  if (code === 'PAYE') return -1;
  const i = DEDUCTION_TYPE_ORDER.indexOf(code.toLowerCase() as DeductionType);
  return i >= 0 ? i : 1000;
}

interface Acc { amount: number; balance?: number; labels: Set<string> }

/** staffId → code → summed deduction lines. */
function index(run: RunSlip[]): Map<string, Map<string, Acc>> {
  const out = new Map<string, Map<string, Acc>>();
  for (const slip of run) {
    const byCode = out.get(slip.staffId) ?? out.set(slip.staffId, new Map()).get(slip.staffId)!;
    for (const l of slip.lines) {
      if (l.kind !== 'deduction') continue;
      const a = byCode.get(l.code) ?? byCode.set(l.code, { amount: 0, labels: new Set() }).get(l.code)!;
      a.amount += Number(l.amount) || 0;
      if (typeof l.remainingBalance === 'number') a.balance = (a.balance ?? 0) + l.remainingBalance;
      if (l.label) a.labels.add(l.label);
    }
  }
  return out;
}

/**
 * Every collection in this run, each beside last month's.
 *
 * A collection appears when either month deducted anything under it, so a
 * deduction that vanished entirely still gets a card showing P0 — that is
 * the case most worth seeing.
 */
export function buildCollections(thisRun: RunSlip[], lastRun: RunSlip[]): Collection[] {
  const now = index(thisRun);
  const last = index(lastRun);
  const names = new Map<string, string>();
  for (const s of [...lastRun, ...thisRun]) names.set(s.staffId, s.name);

  const codes = new Map<string, string>(); // code → a fallback label
  for (const m of [now, last]) m.forEach((byCode) => byCode.forEach((a, code) => {
    if (!codes.has(code)) codes.set(code, [...a.labels][0] ?? code);
  }));

  const out: Collection[] = [];
  codes.forEach((fallback, code) => {
    const label = labelFor(code, fallback);
    const staffIds = new Set<string>();
    now.forEach((m, id) => { if (m.has(code)) staffIds.add(id); });
    last.forEach((m, id) => { if (m.has(code)) staffIds.add(id); });

    const counts = { added: 0, stopped: 0, owing: 0, changed: 0 };
    let carriesBalance = false;
    const rows: CollectionRow[] = [];
    staffIds.forEach((id) => {
      const a = now.get(id)?.get(code);
      const b = last.get(id)?.get(code);
      const n = round2(a?.amount ?? 0);
      const l = round2(b?.amount ?? 0);
      if (a?.balance !== undefined || b?.balance !== undefined) carriesBalance = true;
      let kind: ChangeKind = 'same';
      if (l < CENT && n >= CENT) kind = 'added';
      else if (l >= CENT && n < CENT) kind = (b?.balance ?? 0) >= CENT ? 'owing' : 'stopped';
      else if (Math.abs(n - l) >= CENT) kind = 'changed';
      if (kind !== 'same') counts[kind] += 1;
      if (n < CENT && l < CENT) return; // a zero line both months (PAYE under the threshold)
      const labels = new Set([...(a?.labels ?? []), ...(b?.labels ?? [])]);
      const detail = [...labels].filter((x) => x.toLowerCase() !== label.toLowerCase()).join(', ');
      rows.push({
        staffId: id,
        name: names.get(id) ?? id,
        last: l,
        now: n,
        change: round2(n - l),
        ...(a?.balance !== undefined ? { balanceLeft: round2(a.balance) } : {}),
        ...(b?.balance !== undefined ? { lastBalance: round2(b.balance) } : {}),
        ...(detail ? { detail } : {}),
        kind,
      });
    });
    if (!rows.length) return;
    const totalNow = round2(rows.reduce((s, r) => s + r.now, 0));
    const totalLast = round2(rows.reduce((s, r) => s + r.last, 0));
    out.push({
      key: code,
      label,
      carriesBalance,
      last: totalLast,
      now: totalNow,
      change: round2(totalNow - totalLast),
      staffCount: rows.filter((r) => r.now >= CENT).length,
      rows,
      counts,
      state: counts.owing ? 'bad' : (counts.added || counts.stopped || counts.changed) ? 'warn' : 'ok',
    });
  });
  return out.sort((a, b) => orderOf(a.key) - orderOf(b.key) || a.label.localeCompare(b.label));
}

/** Rows to look at first: owing, stopped, new, changed, then the rest. */
export function rowsByConcern(rows: CollectionRow[]): CollectionRow[] {
  const rank: Record<ChangeKind, number> = { owing: 0, stopped: 1, added: 2, changed: 3, same: 4 };
  return [...rows].sort((a, b) => rank[a.kind] - rank[b.kind] || a.name.localeCompare(b.name));
}

/** One plain sentence for a card: what changed since last month. */
export function changeSummary(c: Collection): string {
  const bits: string[] = [];
  if (c.counts.owing) bits.push(`${c.counts.owing} stopped with money owing`);
  if (c.counts.stopped) bits.push(`${c.counts.stopped} ${c.carriesBalance ? 'paid off' : 'stopped'}`);
  if (c.counts.added) bits.push(`${c.counts.added} new`);
  if (c.counts.changed) bits.push(`${c.counts.changed} changed`);
  return bits.length ? bits.join(', ') : 'Same people, same amounts as last month';
}

/* ------------------------------------------------------------------
   Ticks
   ------------------------------------------------------------------ */

export interface CollectionTick {
  /** The total the person ticked against. */
  total: number;
  by: string;
  byName: string;
  at: string;
}

/**
 * A tick holds only while the total it was given against still stands.
 * Regenerate a payslip, change a loan, and the total moves — the tick
 * lapses and the collection has to be checked again.
 */
export function tickHolds(tick: CollectionTick | undefined, c: Collection): boolean {
  return !!tick && Math.abs(tick.total - c.now) < CENT;
}

/** Ticked once, but against a total that has since changed. */
export function tickLapsed(tick: CollectionTick | undefined, c: Collection): boolean {
  return !!tick && !tickHolds(tick, c);
}

export function allTicked(collections: Collection[], ticks: Record<string, CollectionTick | undefined>): boolean {
  return collections.length > 0 && collections.every((c) => tickHolds(ticks[c.key], c));
}
